import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, ArrowLeft, Mail, User, MessageSquare, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Strict client-side helpers — keep these aligned with the backend rules.
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_DIGITS_RE = /^\+?\d{7,16}$/;

const Contact = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: '',
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    // Live-clear the error for this field as the user fixes it
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const message = formData.message.trim();

    if (name.length < 2) next.name = 'Please enter your full name.';
    if (!email) next.email = 'Email is required.';
    else if (!EMAIL_RE.test(email)) next.email = 'Please enter a valid email address (e.g. you@gmail.com).';
    if (phone) {
      const digitsOnly = phone.replace(/[\s\-().]/g, '');
      if (!PHONE_DIGITS_RE.test(digitsOnly)) {
        next.phone = 'Please enter a valid phone number (digits only, 7–16 chars; "+" allowed).';
      }
    }
    if (message.length < 5) next.message = 'Please write a longer message so we can help you.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast({
        title: 'Please fix the highlighted fields',
        description: 'A couple of fields need attention before we can send your message.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        subject: formData.subject,
        message: formData.message.trim(),
      });

      setSubmitted(true);
      toast({
        title: 'Message Sent!',
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
    } catch (error) {
      console.error('Contact form error:', error);
      const detail = error.response?.data?.detail;
      const msg =
        typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join(' ')
        : 'Something went wrong. Please try again.';
      toast({
        title: 'Failed to Send',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-green-500/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Message Sent!</h1>
          <p className="text-gray-400 mb-8">
            Thank you for reaching out. We&apos;ve received your message and will get back to you as soon as possible.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="contact-success-home"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const fieldClass = (field) =>
    `bg-white/10 border ${errors[field] ? 'border-red-500/70 focus:border-red-400' : 'border-white/20'} text-white placeholder:text-gray-500`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-20 pb-12">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-red-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-orange-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
      </div>

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="text-white hover:text-gray-300 transition-colors mb-6 inline-flex items-center"
            data-testid="contact-back"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </button>

          <div className="text-center">
            <div className="inline-flex items-center justify-center p-3 bg-red-600/20 rounded-xl mb-4">
              <Mail className="h-10 w-10 text-red-400" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">Contact Us</h1>
            <p className="text-gray-400">Have a question or feedback? We&apos;d love to hear from you.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" noValidate data-testid="contact-form">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white flex items-center">
                <User className="h-4 w-4 mr-2 text-gray-400" /> Your Name *
              </Label>
              <Input
                id="name" name="name"
                value={formData.name} onChange={handleInputChange}
                placeholder="Enter your full name"
                className={fieldClass('name')}
                data-testid="contact-name"
                required minLength={2}
              />
              {errors.name && <p className="text-xs text-red-400" data-testid="contact-error-name">{errors.name}</p>}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white flex items-center">
                <Mail className="h-4 w-4 mr-2 text-gray-400" /> Email Address *
              </Label>
              <Input
                id="email" name="email" type="email"
                value={formData.email} onChange={handleInputChange}
                placeholder="you@gmail.com"
                className={fieldClass('email')}
                data-testid="contact-email"
                required
                inputMode="email" autoComplete="email"
              />
              {errors.email && <p className="text-xs text-red-400" data-testid="contact-error-email">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white flex items-center">
                <Phone className="h-4 w-4 mr-2 text-gray-400" /> Phone Number{' '}
                <span className="ml-1 text-gray-500 text-xs">(optional)</span>
              </Label>
              <Input
                id="phone" name="phone" type="tel"
                value={formData.phone} onChange={handleInputChange}
                placeholder="+1 555 123 4567"
                className={fieldClass('phone')}
                data-testid="contact-phone"
                inputMode="tel" autoComplete="tel"
              />
              {errors.phone && <p className="text-xs text-red-400" data-testid="contact-error-phone">{errors.phone}</p>}
              <p className="text-xs text-gray-500">Optional — but helps us reply faster if you&apos;d prefer a call.</p>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject" className="text-white">Subject</Label>
              <select
                id="subject" name="subject"
                value={formData.subject} onChange={handleInputChange}
                className="w-full h-10 px-3 bg-white/10 border border-white/20 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="contact-subject"
              >
                <option value="General Inquiry" className="bg-gray-900">General Inquiry</option>
                <option value="Technical Support" className="bg-gray-900">Technical Support</option>
                <option value="Billing Question" className="bg-gray-900">Billing Question</option>
                <option value="Partnership" className="bg-gray-900">Partnership Opportunity</option>
                <option value="Feedback" className="bg-gray-900">Feedback &amp; Suggestions</option>
                <option value="Other" className="bg-gray-900">Other</option>
              </select>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message" className="text-white flex items-center">
                <MessageSquare className="h-4 w-4 mr-2 text-gray-400" /> Your Message *
              </Label>
              <Textarea
                id="message" name="message"
                value={formData.message} onChange={handleInputChange}
                placeholder="Tell us how we can help you..."
                className={`${fieldClass('message')} min-h-[150px]`}
                data-testid="contact-message"
                required
              />
              {errors.message && <p className="text-xs text-red-400" data-testid="contact-error-message">{errors.message}</p>}
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-semibold py-6 text-lg shadow-lg shadow-red-600/30"
            data-testid="contact-submit"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Sending Message...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Send Message
              </>
            )}
          </Button>

          <p className="text-center text-gray-500 text-sm">
            We typically respond within 24-48 hours.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Contact;
